import moment from 'moment-timezone';
import { compareStrings } from 'utils/compare.js';
import { toSlug } from 'utils';

import {
  MATCH_STATE_ALL,
  MATCH_STATE_UPNEXT,
  MATCH_STATE_LIVE,
  MATCH_STATE_FINISHED,
  MATCH_TYPE_REGULAR
} from 'modules/Football/consts';

import { localLongDate, localYearMonth } from 'utils/LocalDateTimeHelper';

const KNOWN_STATES = {
  all: state => MATCH_STATE_ALL.includes(state),
  upNext: state => MATCH_STATE_UPNEXT.includes(state),
  ongoing: state => MATCH_STATE_LIVE.includes(state),
  finished: state => MATCH_STATE_FINISHED.includes(state)
};

export function filterMatchesByState(matches = [], filter) {
  return matches.filter(match => KNOWN_STATES[filter] && KNOWN_STATES[filter](match.state));
}

export function mapMatchesToDates(matches = [], fixturesResults) {
  const dates = {};

  matches.map(match => {

    const {
      match_reference,
      match_date,
      match_betting_market = {},
      state,
      match_type = MATCH_TYPE_REGULAR,
      round = {},
      clock,
      team_score_a = {},
      team_score_b = {},
      penalty_shootout_score = {},
      competition = {},
      match_outcome = {}
    } = match || {};

    const day = moment(match_date).startOf('day').format('YYYYMMDD');

    if (!dates[day]) {
      dates[day] = {
        name: localLongDate(match_date),
        id: match_date,
        day,
        matchList: []
      }
    }

    dates[day].matchList.push({
      match_reference,
      match_date,
      match_betting_market,
      state,
      clock,
      match_type,
      round,
      team_score_a,
      team_score_b,
      penalty_shootout_score,
      competition,
      match_outcome
    });
  });

  const sortedDates = Object.keys(dates).sort().map(day => dates[day]);

  sortedDates.forEach(date => date.matchList.sort((a,b) => sortWithinGrouping(a,b)));

  if (fixturesResults == "results") {
    return sortedDates.reverse();
  } else {
    return sortedDates.filter(date => date.day >= moment().format('YYYYMMDD'));
  }
}

export function mapMatchesToCompetitions(matches = []) {
  const competitions = {};

  matches.map(match => {

    const {
      match_reference,
      match_date,
      match_betting_market = {},
      state,
      clock,
      match_type = MATCH_TYPE_REGULAR,
      round = {},
      match_outcome = {},
      team_score_a = {},
      team_score_b = {},
      penalty_shootout_score = {},
      competition: {
        competition_reference: {
          id: competitionId
        } = {},
        name: competitionName,
        sort_order: competitionSortOrder = -1
      } = {}
    } = match || {};

    if (!competitions[competitionId]) {
      competitions[competitionId] = {
        name: competitionName,
        id: competitionId,
        sortOrder: competitionSortOrder,
        matchList: []
      };
    }

    competitions[competitionId].matchList.push({
      match_reference,
      match_date,
      match_betting_market,
      state,
      match_type,
      round,
      clock,
      match_outcome,
      team_score_a,
      team_score_b,
      penalty_shootout_score
    });
  });

  const sortedCompetitions = Object.keys(competitions).map(id => competitions[id]).sort((a, b) => {
    if (a.sortOrder === b.sortOrder) {
      return compareStrings(a.name, b.name);
    }
    else if (a.sortOrder === -1) {
      return 1;
    }
    else if (b.sortOrder === -1) {
      return -1;
    }
    else {
      return a.sortOrder - b.sortOrder;
    }
  });

  sortedCompetitions.forEach(competition => competition.matchList.sort((a,b) => sortWithinGrouping(a,b)));

  return sortedCompetitions;
}

export function mapMatchesToCompetitionRounds(matches = []) {
  function assignToRound(rounds, match) {
    const {round, match_type} = match;

    const roundKey = getRoundKey(match_type, round); 

    if (!rounds.has(roundKey)) {
      rounds.set(roundKey, {matchList: []});
    }

    rounds.get(roundKey).matchList.push(match);
    rounds.get(roundKey).round = round;
    return rounds;
  }

  const rounds = matches.reduce(assignToRound, new Map());
  //rounds.entries().forEach(round => round.matchList.sort((a,b) => sortWithinGrouping(a,b)));

  return rounds;
}

function getRoundKey(matchType, round, defaultKey = MATCH_TYPE_REGULAR) {
  if (matchType != MATCH_TYPE_REGULAR && round && round.round_type) {
    return `${round.round_type}-${round.round_number || "0"}`;
  } 

  return defaultKey;
}

function sortWithinGrouping(a, b) {
  const dateDiff = new Date(a.match_date) - new Date(b.match_date);

  if (dateDiff === 0) {
    const {
      team_score_a: {
        team: {
          name: teamNameA
        } = {}
      } = {}
    } = a;

    const {
      team_score_a: {
        team: {
          name: teamNameB
        } = {}
      } = {}
    } = b;
    return compareStrings(teamNameA, teamNameB);
  }

  return dateDiff;
}



export function getMonths(competitionStart, competitionEnd, fixturesResults) {

    if (competitionStart && competitionEnd) {
      let date, end, months = [], now = moment();

      if (fixturesResults == "results") {
        date = moment(competitionStart).startOf('month');
        end = moment(competitionEnd).isBefore(now) ? moment(competitionEnd) : now;
      } else {
        date = moment(competitionStart).isAfter(now) ? moment(competitionStart) : now;
        end = moment(competitionEnd);
      }

      while (!date.isAfter(end)) {
        months.push(localYearMonth(date));
        date = date.add(1, 'month').startOf('month');
      }

      return months;
    } else {
      return [];
    }

  }

export function getCompetitionsNavList(competitions, fixturesResults) {
    return competitions.map(({
      name,
      competition_reference: {
        id: competition_id
       }
      }) => ({
       text: name,
       href: `/football/${fixturesResults}/competitions/${toSlug(name)}/${competition_id}`
      })
    );
}

export function getTeamsNavList(teams, fixturesResults, competitionName, competitionId) {
    const teamsList = teams.map(({
      name,
      team_reference: {
        id: team_id
      }
    }) => ({
        text: name,
        href: `/football/${fixturesResults}/teams/${toSlug(name)}/${team_id}/${toSlug(competitionName)}/${competitionId}`
    }));

    return teamsList.sort((a,b) => compareStrings(a.text,b.text));
}



// WEBPACK FOOTER //
// ./modules/Football/utils.js